'use client';

import DiscountCountdown from '@/app/(public)/pricing/discount-countdown';
import {
  buildFeatures,
  computePriceInfo,
  getCtaState,
  getEffectiveDiscount,
} from '@/app/(public)/pricing/plan-utils';
import type { Plan, PlanSku } from '@/app/actions/cart';
import type { User } from '@/app/actions/user';
import { Button } from '@/components/ui/button';
import { formatPrice } from '@/lib/util';
import { cn } from '@/lib/utils';
import NumberFlow from '@number-flow/react';
import { CheckIcon, CrownIcon } from 'lucide-react';
import { motion } from 'motion/react';

const cardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: { opacity: 1, y: 0, scale: 1 },
};

interface PlanCardProps {
  plan: Plan;
  selectedPeriod: string;
  user: User | null;
  onBuy: (plan: Plan, sku: PlanSku) => void;
  onLogin?: () => void;
  index: number;
}

const PlanCard = ({
  plan,
  selectedPeriod,
  user,
  onBuy,
  onLogin,
  index,
}: PlanCardProps) => {
  const isFree =
    plan.tier_rank === 0 || plan.skus.every((s) => s.sale_price === 0);
  const selectedSku = isFree
    ? undefined
    : plan.skus.find((s) => s.validity_period === selectedPeriod);
  const isFeatured = !isFree && (selectedSku?.recommended ?? false);
  const hasDiscount =
    !isFree &&
    selectedSku != null &&
    getEffectiveDiscount(plan, selectedSku) != null;
  const discount = selectedSku ? getEffectiveDiscount(plan, selectedSku) : null;
  const isHighlighted = isFeatured;
  const features = buildFeatures(plan);
  const cta = getCtaState(plan, isFree, !!selectedSku, user);
  const priceInfo =
    !isFree && selectedSku ? computePriceInfo(plan, selectedSku) : null;

  return (
    <motion.div
      key={plan.product_id}
      variants={cardVariants}
      className="relative"
    >
      {/* 推荐标签 */}
      {isHighlighted && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{
            delay: 0.4 + index * 0.1,
            duration: 0.4,
          }}
          className="absolute -top-4 left-1/2 -translate-x-1/2 z-10"
        >
          <div
            className={cn(
              'px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 shadow-lg text-white',
              'bg-linear-to-r from-primary to-primary text-primary-foreground',
            )}
          >
            <CrownIcon className="w-3.5 h-3.5" />
            推荐
          </div>
        </motion.div>
      )}

      <div
        className={cn(
          'relative h-full p-8 rounded-xl border transition-all duration-300 hover:shadow-md flex flex-col',
          isFeatured
            ? 'border-2 border-primary bg-linear-to-br from-primary/5 to-primary/10 shadow-lg'
            : hasDiscount
              ? 'border-2 border-orange-200/80 bg-linear-to-b from-card to-orange-50/40 dark:border-orange-900/40 dark:to-orange-950/10'
              : 'border-border bg-card',
        )}
      >
        {/* 方案头部 */}
        <div className="text-center space-y-4 mb-8">
          <h3 className="text-2xl font-bold text-foreground">{plan.name}</h3>
          {plan.description && (
            <p className="text-sm text-muted-foreground">{plan.description}</p>
          )}

          {/* 价格 */}
          {isFree ? (
            <div className="mt-4">
              <span className="text-3xl font-bold text-foreground">¥0.00</span>
            </div>
          ) : priceInfo ? (
            <div className="mt-4 space-y-1.5">
              {hasDiscount && discount && (
                <div className="inline-flex rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700 dark:border-orange-900/60 dark:bg-orange-950/30 dark:text-orange-300">
                  {discount.name}
                </div>
              )}
              {priceInfo.discountSaved != null && (
                <div className="text-sm text-muted-foreground line-through h-5">
                  {formatPrice(
                    priceInfo.months > 1
                      ? Math.round(
                          priceInfo.priceBeforeDiscount / priceInfo.months,
                        )
                      : priceInfo.priceBeforeDiscount,
                  )}
                  /月
                </div>
              )}
              <div className="text-3xl lg:text-4xl font-bold text-foreground flex items-center justify-center">
                <NumberFlow
                  value={priceInfo.monthlyPrice / 100}
                  format={{
                    style: 'currency',
                    currency: 'CNY',
                    trailingZeroDisplay: 'stripIfInteger',
                  }}
                  suffix="/月"
                />
              </div>
              {priceInfo.months > 1 && (
                <div className="text-sm text-muted-foreground flex items-center justify-center gap-1.5">
                  <span>
                    {formatPrice(priceInfo.totalPrice)}/
                    {priceInfo.periodLabel}
                  </span>
                  {priceInfo.periodSaved != null &&
                    priceInfo.periodSaved > 0 && (
                      <span className="text-xs font-medium bg-green-100 dark:bg-green-900/80 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded-full">
                        省 {formatPrice(priceInfo.periodSaved)}
                      </span>
                    )}
                </div>
              )}
              {priceInfo.discountSaved != null && (
                <div className="text-xs font-medium flex items-center justify-center gap-1.5 flex-wrap">
                  <DiscountCountdown endTime={priceInfo.discount!.end_time} />
                </div>
              )}
            </div>
          ) : (
            <div className="mt-4">
              <span className="text-sm text-muted-foreground">
                暂无该周期方案
              </span>
            </div>
          )}
        </div>

        {/* CTA 按钮 */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 + index * 0.1 }}
          className="mb-8"
        >
          {!cta.hidden && (
            <Button
              className="w-full"
              size="lg"
              variant={cta.disabled ? 'outline' : 'default'}
              onClick={() => {
                if (cta.action === 'login') onLogin?.();
                else if (cta.action === 'buy' && selectedSku)
                  onBuy(plan, selectedSku);
              }}
              disabled={cta.disabled}
            >
              {cta.label}
            </Button>
          )}
        </motion.div>

        {/* 功能列表 */}
        <div className="space-y-3 flex-1">
          <h4 className="text-sm font-semibold text-foreground border-b pb-2">
            功能特权
          </h4>
          {features.map((feature, featureIndex) => (
            <motion.div
              key={featureIndex}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                delay: 0.5 + index * 0.08 + featureIndex * 0.04,
              }}
              className="flex items-center gap-3"
            >
              <div className="shrink-0 w-5 h-5 flex items-center justify-center">
                <CheckIcon className="w-3.5 h-3.5 text-foreground" />
              </div>
              <span className="text-sm text-secondary-foreground">
                {feature}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default PlanCard;
